import { useEffect, useState } from 'react';

/**
 * useResponsive — three-way breakpoint classifier.
 *
 * Returns the active breakpoint plus boolean convenience flags and the raw
 * viewport width. Single resize listener regardless of how many booleans the
 * caller reads (vs. calling useMobile(768) + useMobile(1024) separately,
 * which installed two listeners per component).
 *
 * Breakpoints:
 *   mobile  : width <  768
 *   tablet  : width >= 768 && width < 1024
 *   desktop : width >= 1024
 */

export const BREAKPOINTS = {
  MOBILE_MAX: 767,
  TABLET_MIN: 768,
  TABLET_MAX: 1023,
  DESKTOP_MIN: 1024,
};

function classify(width) {
  if (width < BREAKPOINTS.TABLET_MIN) return 'mobile';
  if (width < BREAKPOINTS.DESKTOP_MIN) return 'tablet';
  return 'desktop';
}

function readWidth() {
  return typeof window !== 'undefined' ? window.innerWidth : BREAKPOINTS.DESKTOP_MIN;
}

export default function useResponsive() {
  const [width, setWidth] = useState(readWidth);

  useEffect(() => {
    const onResize = () => setWidth(readWidth());
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const breakpoint = classify(width);

  return {
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop',
    width,
  };
}
